export type FacebookEmbedKind = "post" | "video";

export type ParsedFacebookUrl = {
  id: string;
  kind: FacebookEmbedKind;
  url: string;
};

const FACEBOOK_HOST = /^(?:www\.)?facebook\.com$/i;

function normalizeFacebookHost(url: string): string {
  try {
    const parsed = new URL(url.trim());
    if (!FACEBOOK_HOST.test(parsed.hostname)) {
      return url.trim();
    }
    parsed.hostname = "www.facebook.com";
    parsed.protocol = "https:";
    return parsed.toString().replace(/\/$/, "");
  } catch {
    return url.trim();
  }
}

export function isFacebookShareVideoUrl(url: string): boolean {
  try {
    const parsed = new URL(url.trim());
    return FACEBOOK_HOST.test(parsed.hostname) && /^\/share\/v\//i.test(parsed.pathname);
  } catch {
    return false;
  }
}

export function parseFacebookUrl(url: string): ParsedFacebookUrl | null {
  const trimmed = url.trim();

  try {
    const parsed = new URL(trimmed);
    const host = parsed.hostname.toLowerCase();

    if (host === "fb.watch") {
      const id = parsed.pathname.replace(/^\//, "").split("/")[0];
      if (!id) return null;
      return { id, kind: "video", url: normalizeFacebookHost(trimmed) };
    }

    if (!FACEBOOK_HOST.test(host)) {
      return null;
    }

    const path = parsed.pathname;

    const shareMatch = path.match(/^\/share\/v\/([^/?&#]+)/i);
    if (shareMatch?.[1]) {
      return {
        id: shareMatch[1],
        kind: "video",
        url: normalizeFacebookHost(trimmed),
      };
    }

    const reelMatch = path.match(/\/reels?\/([^/?&#]+)/i);
    if (reelMatch?.[1]) {
      const canonical = `https://www.facebook.com/reel/${reelMatch[1]}`;
      return { id: reelMatch[1], kind: "video", url: canonical };
    }

    if (path === "/watch" || path === "/watch/") {
      const videoId = parsed.searchParams.get("v");
      if (videoId) {
        return {
          id: videoId,
          kind: "video",
          url: `https://www.facebook.com/watch?v=${videoId}`,
        };
      }
    }

    const videoMatch = path.match(/\/videos\/([^/?&#]+)/i);
    if (videoMatch?.[1]) {
      return {
        id: videoMatch[1],
        kind: "video",
        url: normalizeFacebookHost(trimmed),
      };
    }

    const postMatch = path.match(/\/posts\/([^/?&#]+)/i);
    if (postMatch?.[1]) {
      return {
        id: postMatch[1],
        kind: "post",
        url: normalizeFacebookHost(trimmed),
      };
    }

    return null;
  } catch {
    return null;
  }
}

export function getFacebookVideoIframeSrc(url: string): string {
  return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false&width=550`;
}
